import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, FileText, TrendUp, TrendDown, Minus, Camera, Trash, Package, Printer, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatApiError, API } from "../lib/api";
import { useTableSort } from "../lib/useTableSort";
import { SortHeader } from "../components/SortHeader";

function fmtDate(iso) {
  if (!iso) return "—";
  return iso.length >= 10 ? iso.slice(0, 10) : iso;
}
function fmtPrice(v) {
  if (v == null) return "—";
  return Number(v).toFixed(2) + " €";
}

export default function AdminSupplierDetail() {
  const { id } = useParams();
  const [supplier, setSupplier] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [history, setHistory] = useState([]);
  const [items, setItems] = useState([]);
  const [tab, setTab] = useState("facturas"); // facturas | albaranes | history | items
  const [openInvoice, setOpenInvoice] = useState(null);
  const histSort = useTableSort(history.map((h) => ({ ...h, n_invoices: h.history.length })), "descripcion", "asc");
  const linesSort = useTableSort(openInvoice?.lines || [], null);
  const [confirmDel, setConfirmDel] = useState(null);
  const [uploading, setUploading] = useState(false);

  const load = useCallback(async () => {
    try {
      const [sRes, iRes, hRes, itRes] = await Promise.all([
        api.get("/suppliers"),
        api.get("/invoices", { params: { supplier_id: id } }),
        api.get(`/suppliers/${id}/price-history`),
        api.get(`/suppliers/${id}/items`),
      ]);
      const found = sRes.data.find((s) => s.id === id);
      if (!found) {
        toast.error("Proveedor no encontrado");
        return;
      }
      setSupplier(found);
      setInvoices(iRes.data);
      setHistory(hRes.data);
      setItems(itRes.data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      toast.info("Analizando documento…");
      const { data } = await api.post("/invoices/analyze", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const dt = data.invoice?.document_type === "albaran" ? "Albarán" : "Factura";
      if (data.supplier.id !== id) {
        toast.warning(`${dt} asignado a "${data.supplier.name}" (proveedor detectado distinto).`);
      } else {
        toast.success(`${dt} añadido correctamente`);
      }
      load();
    } catch (e2) {
      toast.error(formatApiError(e2));
    } finally {
      setUploading(false);
    }
  };

  const doDelete = async () => {
    if (!confirmDel) return;
    try {
      await api.delete(`/invoices/${confirmDel.id}`);
      toast.success(confirmDel.document_type === "albaran" ? "Albarán eliminado" : "Factura eliminada");
      setConfirmDel(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (!supplier) return <div className="text-[var(--text-secondary)]">Cargando…</div>;

  const facturas = invoices.filter((inv) => (inv.document_type || "factura") === "factura");
  const albaranes = invoices.filter((inv) => inv.document_type === "albaran");

  return (
    <div>
      <Link to="/admin/suppliers" className="inline-flex items-center gap-2 text-sm text-[var(--text-secondary)] hover:text-[var(--primary)] mb-4">
        <ArrowLeft size={16} /> Volver a proveedores
      </Link>

      <div className="flex flex-wrap justify-between items-start gap-4 mb-8">
        <div>
          <div className="label-cap">proveedor</div>
          <h1 className="font-display text-4xl font-bold tracking-tight">{supplier.name}</h1>
          <div className="text-sm text-[var(--text-secondary)] mt-1 space-y-0.5">
            <div>
              {supplier.cif && <>CIF: <span className="font-mono">{supplier.cif}</span> · </>}
              {supplier.phone || "—"}
              {supplier.address && <> · {supplier.address}</>}
            </div>
            {(supplier.contact_person || supplier.contact_phone) && (
              <div>Contacto: {supplier.contact_person || "—"} {supplier.contact_phone && `(${supplier.contact_phone})`}</div>
            )}
            {(supplier.order_day || supplier.supply_day) && (
              <div>
                {supplier.order_day && <>📅 Pedido: <strong className="capitalize">{supplier.order_day}</strong></>}
                {supplier.order_day && supplier.supply_day && " · "}
                {supplier.supply_day && <>🚚 Suministro: <strong className="capitalize">{supplier.supply_day}</strong></>}
              </div>
            )}
            {supplier.default_iva_pct != null && <div>IVA por defecto: {supplier.default_iva_pct}%</div>}
          </div>
        </div>
        <label className={`btn-primary px-5 py-3 rounded-xl font-display font-bold flex items-center gap-2 cursor-pointer ${uploading ? "opacity-60 pointer-events-none" : ""}`}>
          <Camera size={18} weight="duotone" />
          {uploading ? "Analizando…" : "Añadir documento"}
          <input type="file" accept="image/*" capture="environment" onChange={onUpload} className="hidden" data-testid="supplier-upload-invoice" />
        </label>
      </div>

      <div className="flex gap-2 mb-6 no-print flex-wrap">
        <button onClick={() => setTab("facturas")} data-testid="tab-facturas" className={`px-4 py-2 rounded-lg font-display font-semibold ${tab === "facturas" ? "bg-[var(--primary)] text-white" : "border border-[var(--border)]"}`}>
          Facturas ({facturas.length})
        </button>
        <button onClick={() => setTab("albaranes")} data-testid="tab-albaranes" className={`px-4 py-2 rounded-lg font-display font-semibold ${tab === "albaranes" ? "bg-[var(--primary)] text-white" : "border border-[var(--border)]"}`}>
          Albaranes ({albaranes.length})
        </button>
        <button onClick={() => setTab("items")} data-testid="tab-items" className={`px-4 py-2 rounded-lg font-display font-semibold ${tab === "items" ? "bg-[var(--primary)] text-white" : "border border-[var(--border)]"}`}>
          Artículos ({items.length})
        </button>
        <button onClick={() => setTab("history")} data-testid="tab-history" className={`px-4 py-2 rounded-lg font-display font-semibold ${tab === "history" ? "bg-[var(--primary)] text-white" : "border border-[var(--border)]"}`}>
          Historial de precios ({history.length})
        </button>
      </div>

      {tab === "items" && (
        <SupplierItemsTab
          supplier={supplier}
          items={items}
          reload={load}
        />
      )}

      {tab === "facturas" && (
        <InvoiceListTable
          docs={facturas}
          docLabel="factura"
          emptyText="Sin facturas. Sube una foto para empezar."
          onView={setOpenInvoice}
          onDelete={setConfirmDel}
          supplierPaymentMethod={supplier.payment_method || "transferencia"}
          onTogglePaid={async (inv) => {
            try {
              await api.patch(`/invoices/${inv.id}/paid`, { paid: !inv.paid });
              load();
            } catch (e) {
              toast.error(formatApiError(e));
            }
          }}
        />
      )}

      {tab === "albaranes" && (
        <InvoiceListTable
          docs={albaranes}
          docLabel="albarán"
          emptyText="Sin albaranes. Sube una foto para empezar."
          onView={setOpenInvoice}
          onDelete={setConfirmDel}
        />
      )}

      {tab === "history" && (
        <div className="card-flat overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-[var(--border)] bg-[var(--bg)]">
                <th className="text-left p-4"><SortHeader sortKey="descripcion" currentKey={histSort.sortKey} currentDir={histSort.sortDir} onClick={histSort.toggleSort}>Artículo</SortHeader></th>
                <th className="text-right p-4"><SortHeader align="right" sortKey="first_price" currentKey={histSort.sortKey} currentDir={histSort.sortDir} onClick={histSort.toggleSort}>Precio inicial</SortHeader></th>
                <th className="text-right p-4"><SortHeader align="right" sortKey="last_price" currentKey={histSort.sortKey} currentDir={histSort.sortDir} onClick={histSort.toggleSort}>Precio actual</SortHeader></th>
                <th className="text-right p-4"><SortHeader align="right" sortKey="price_change_pct" currentKey={histSort.sortKey} currentDir={histSort.sortDir} onClick={histSort.toggleSort}>Variación</SortHeader></th>
                <th className="text-right p-4"><SortHeader align="right" sortKey="min_price" currentKey={histSort.sortKey} currentDir={histSort.sortDir} onClick={histSort.toggleSort}>Mín / Máx</SortHeader></th>
                <th className="text-right p-4"><SortHeader align="right" sortKey="n_invoices" currentKey={histSort.sortKey} currentDir={histSort.sortDir} onClick={histSort.toggleSort}>Facturas</SortHeader></th>
              </tr>
            </thead>
            <tbody>
              {history.length === 0 && (
                <tr><td colSpan={6} className="p-8 text-center text-[var(--text-secondary)]">Sin artículos aún. Añade al menos una factura.</td></tr>
              )}
              {histSort.sortedRows.map((h) => {
                const pct = h.price_change_pct;
                const Icon = pct == null || pct === 0 ? Minus : pct > 0 ? TrendUp : TrendDown;
                const color = pct == null || pct === 0 ? "var(--text-secondary)" : pct > 0 ? "var(--danger)" : "var(--primary)";
                return (
                  <tr key={h.descripcion_norm} className="border-b border-[var(--border)] last:border-0 hover:bg-[var(--bg)]">
                    <td className="p-4 font-display font-semibold max-w-md">{h.descripcion}</td>
                    <td className="p-4 text-right font-mono">{fmtPrice(h.first_price)}</td>
                    <td className="p-4 text-right font-mono font-semibold">{fmtPrice(h.last_price)}</td>
                    <td className="p-4 text-right font-mono" style={{ color }}>
                      <span className="inline-flex items-center gap-1">
                        <Icon size={14} weight="duotone" />
                        {pct == null ? "—" : `${pct > 0 ? "+" : ""}${pct.toFixed(1)}%`}
                      </span>
                    </td>
                    <td className="p-4 text-right font-mono text-xs text-[var(--text-secondary)]">
                      {fmtPrice(h.min_price)} / {fmtPrice(h.max_price)}
                    </td>
                    <td className="p-4 text-right font-mono">{h.history.length}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {openInvoice && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-auto">
          <div className="card-flat w-full max-w-4xl p-6 my-8 max-h-[90vh] overflow-auto">
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="label-cap">{openInvoice.document_type === "albaran" ? "albarán" : "factura"}</div>
                <h2 className="font-display text-2xl font-bold tracking-tight">Nº {openInvoice.invoice_number || "sin número"}</h2>
                <div className="text-sm text-[var(--text-secondary)]">{fmtDate(openInvoice.invoice_date)} · Total {fmtPrice(openInvoice.total)}</div>
              </div>
              <button onClick={() => setOpenInvoice(null)} className="p-2 rounded-full hover:bg-[var(--border)]">✕</button>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div>
                <div className="label-cap mb-2">líneas</div>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-[var(--border)]">
                      <th className="text-left p-2"><SortHeader sortKey="descripcion" currentKey={linesSort.sortKey} currentDir={linesSort.sortDir} onClick={linesSort.toggleSort}>Descripción</SortHeader></th>
                      <th className="text-right p-2"><SortHeader align="right" sortKey="cantidad" currentKey={linesSort.sortKey} currentDir={linesSort.sortDir} onClick={linesSort.toggleSort}>Cant.</SortHeader></th>
                      <th className="text-right p-2"><SortHeader align="right" sortKey="precio_unitario" currentKey={linesSort.sortKey} currentDir={linesSort.sortDir} onClick={linesSort.toggleSort}>P.U.</SortHeader></th>
                      <th className="text-right p-2"><SortHeader align="right" sortKey="importe" currentKey={linesSort.sortKey} currentDir={linesSort.sortDir} onClick={linesSort.toggleSort}>Importe</SortHeader></th>
                    </tr>
                  </thead>
                  <tbody>
                    {linesSort.sortedRows.map((ln, i) => (
                      <tr key={`${openInvoice.id}-line-${i}`} className="border-b border-[var(--border)]">
                        <td className="p-2">{ln.descripcion}</td>
                        <td className="p-2 text-right font-mono">{ln.cantidad ?? "—"}</td>
                        <td className="p-2 text-right font-mono">{fmtPrice(ln.precio_unitario)}</td>
                        <td className="p-2 text-right font-mono">{fmtPrice(ln.importe)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div>
                <div className="label-cap mb-2">imagen original</div>
                <img
                  src={`${API}/invoices/${openInvoice.id}/image`}
                  alt={openInvoice.document_type === "albaran" ? "Albarán" : "Factura"}
                  className="w-full rounded-lg border border-[var(--border)]"
                  onError={(e) => { e.target.style.display = "none"; }}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {confirmDel && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="card-flat w-full max-w-md p-8">
            <h2 className="font-display text-2xl font-bold tracking-tight mb-2">¿Eliminar {confirmDel.document_type === "albaran" ? "albarán" : "factura"}?</h2>
            <p className="text-sm text-[var(--text-secondary)] mb-6">Nº {confirmDel.invoice_number || "—"}. Esta acción no se puede deshacer.</p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setConfirmDel(null)} className="px-5 py-3 rounded-xl border border-[var(--border)] font-display font-semibold">Cancelar</button>
              <button onClick={doDelete} className="btn-danger px-6 py-3 rounded-xl font-display font-bold">Sí, eliminar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function InvoiceListTable({ docs, docLabel, emptyText, onView, onDelete, supplierPaymentMethod, onTogglePaid }) {
  const numLabel = docLabel === "albarán" ? "Nº albarán" : "Nº factura";
  const isFactura = docLabel === "factura";
  const isDomiciliat = supplierPaymentMethod === "domiciliat";
  const { sortedRows: sortedDocs, sortKey, sortDir, toggleSort } = useTableSort(docs, "invoice_date", "desc");
  return (
    <div className="card-flat overflow-hidden">
      <table className="w-full">
        <thead>
          <tr className="border-b border-[var(--border)] bg-[var(--bg)]">
            <th className="text-left p-4"><SortHeader sortKey="invoice_number" currentKey={sortKey} currentDir={sortDir} onClick={toggleSort}>{numLabel}</SortHeader></th>
            <th className="text-left p-4"><SortHeader sortKey="invoice_date" currentKey={sortKey} currentDir={sortDir} onClick={toggleSort}>Fecha</SortHeader></th>
            <th className="text-right p-4 label-cap">Líneas</th>
            <th className="text-right p-4"><SortHeader align="right" sortKey="total" currentKey={sortKey} currentDir={sortDir} onClick={toggleSort}>Total</SortHeader></th>
            {isFactura && <th className="text-center p-4"><SortHeader align="center" sortKey="paid" currentKey={sortKey} currentDir={sortDir} onClick={toggleSort}>Pagada</SortHeader></th>}
            <th className="text-right p-4 label-cap">Acciones</th>
          </tr>
        </thead>
        <tbody>
          {sortedDocs.length === 0 && (
            <tr><td colSpan={isFactura ? 6 : 5} className="p-8 text-center text-[var(--text-secondary)]">{emptyText}</td></tr>
          )}
          {sortedDocs.map((inv) => (
            <tr key={inv.id} className="border-b border-[var(--border)] last:border-0 hover:bg-[var(--bg)]">
              <td className="p-4 font-mono">{inv.invoice_number || "—"}</td>
              <td className="p-4">{fmtDate(inv.invoice_date)}</td>
              <td className="p-4 text-right font-mono">{inv.lines?.length || 0}</td>
              <td className="p-4 text-right font-mono font-semibold">{fmtPrice(inv.total)}</td>
              {isFactura && (
                <td className="p-4 text-center">
                  {isDomiciliat ? (
                    <span className="inline-flex items-center px-2 py-1 rounded-lg bg-[var(--primary)]/15 text-[var(--primary)] text-xs font-display font-bold uppercase tracking-wider" data-testid={`paid-domiciliat-${inv.id}`}>
                      Domiciliado
                    </span>
                  ) : (
                    <button
                      onClick={() => onTogglePaid?.(inv)}
                      data-testid={`toggle-paid-${inv.id}`}
                      className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-display font-bold uppercase tracking-wider transition ${
                        inv.paid
                          ? "bg-emerald-500/15 text-emerald-600 hover:bg-emerald-500/25 border border-emerald-500/40"
                          : "bg-white text-[var(--text-secondary)] hover:bg-[var(--bg)] border border-[var(--border)]"
                      }`}
                    >
                      {inv.paid ? "✓ Pagada" : "Sin pagar"}
                    </button>
                  )}
                </td>
              )}
              <td className="p-4">
                <div className="flex justify-end gap-2">
                  <button onClick={() => onView(inv)} data-testid={`view-invoice-${inv.id}`} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-[var(--border)] hover:bg-white text-sm font-display font-semibold">
                    Ver
                  </button>
                  <button onClick={() => onDelete(inv)} data-testid={`delete-invoice-${inv.id}`} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-[var(--border)] hover:bg-[var(--danger)] hover:text-white hover:border-[var(--danger)] text-sm font-display font-semibold">
                    <Trash size={14} weight="duotone" />
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SupplierItemsTab({ supplier, items, reload }) {
  const [savingNorm, setSavingNorm] = useState(null);
  const { sortedRows: sortedItems, sortKey, sortDir, toggleSort } = useTableSort(items, "descripcion", "asc");

  const saveMinQty = async (item, newQty) => {
    setSavingNorm(item.descripcion_norm);
    try {
      await api.put(
        `/suppliers/${supplier.id}/items/${encodeURIComponent(item.descripcion_norm)}`,
        { min_qty: newQty === "" || newQty == null ? null : Number(newQty) }
      );
      toast.success("Cantidad mínima guardada");
      reload();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSavingNorm(null);
    }
  };

  const hideItem = async (item) => {
    if (!window.confirm(`Ocultar "${item.descripcion}" del listado?\n\nReaparecerá automáticamente si vuelve a salir en una factura futura.`)) return;
    try {
      await api.delete(
        `/suppliers/${supplier.id}/items/${encodeURIComponent(item.descripcion_norm)}`
      );
      toast.success("Artículo ocultado");
      reload();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4 no-print">
        <div className="text-sm text-[var(--text-secondary)]">
          Los artículos con cantidad mínima definida aparecen primero. Al imprimir se genera la nota de pedido.
        </div>
        <button
          onClick={() => window.print()}
          data-testid="print-order-note"
          className="btn-primary px-5 py-3 rounded-xl font-display font-bold flex items-center gap-2"
        >
          <Printer size={18} weight="duotone" />
          Imprimir nota de pedido
        </button>
      </div>

      <div className="print-only mb-4 hidden">
        <div className="font-display text-2xl font-bold">Nota de pedido — {supplier.name}</div>
        <div className="text-sm text-[var(--text-secondary)]">
          {supplier.contact_person && <>Contacto: {supplier.contact_person} · </>}
          {supplier.contact_phone && <>{supplier.contact_phone} · </>}
          Día de pedido: <span className="capitalize">{supplier.order_day || "—"}</span> · Suministro: <span className="capitalize">{supplier.supply_day || "—"}</span>
        </div>
        <div className="text-xs mt-1">Generado: {new Date().toLocaleString("es-ES")}</div>
      </div>

      <div className="card-flat overflow-hidden" data-testid="supplier-items-table">
        <table className="w-full">
          <thead>
            <tr className="border-b border-[var(--border)] bg-[var(--bg)]">
              <th className="text-left p-4"><SortHeader sortKey="descripcion" currentKey={sortKey} currentDir={sortDir} onClick={toggleSort}>Artículo</SortHeader></th>
              <th className="text-right p-4"><SortHeader align="right" sortKey="last_price" currentKey={sortKey} currentDir={sortDir} onClick={toggleSort}>Último precio</SortHeader></th>
              <th className="text-right p-4 label-cap w-32">Cant. mínima</th>
              <th className="text-left p-4 label-cap">Aviso</th>
              <th className="text-right p-4 label-cap no-print">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {sortedItems.length === 0 && (
              <tr><td colSpan={5} className="p-8 text-center text-[var(--text-secondary)]">Sin artículos aún.</td></tr>
            )}
            {sortedItems.map((it) => {
              const cheap = it.cheapest_alternative;
              return (
                <tr
                  key={it.descripcion_norm}
                  data-testid={`item-row-${it.descripcion_norm}`}
                  className={`border-b border-[var(--border)] last:border-0 ${it.cheaper_elsewhere ? "bg-[#FDECEA]" : ""}`}
                >
                  <td className="p-4">
                    <div className={`font-display font-semibold ${it.cheaper_elsewhere ? "text-[var(--danger)]" : ""}`}>
                      {it.descripcion}
                    </div>
                    {it.last_invoice_date && (
                      <div className="text-xs text-[var(--text-secondary)]">Última factura: {it.last_invoice_date}</div>
                    )}
                  </td>
                  <td className="p-4 text-right font-mono font-semibold whitespace-nowrap">
                    {it.last_price != null ? Number(it.last_price).toFixed(2) + " €" : "—"}
                  </td>
                  <td className="p-4 text-right no-print">
                    <input
                      type="number"
                      step="0.5"
                      min="0"
                      defaultValue={it.min_qty ?? ""}
                      onBlur={(e) => {
                        const v = e.target.value;
                        const orig = it.min_qty == null ? "" : String(it.min_qty);
                        if (v !== orig) saveMinQty(it, v);
                      }}
                      disabled={savingNorm === it.descripcion_norm}
                      data-testid={`min-qty-${it.descripcion_norm}`}
                      className="w-20 px-2 py-1 rounded-lg border border-[var(--border)] bg-white text-right font-mono"
                      placeholder="—"
                    />
                  </td>
                  <td className="p-4 text-right print-only-cell hidden">
                    {it.min_qty != null ? <span className="font-mono">{it.min_qty}</span> : "—"}
                  </td>
                  <td className="p-4">
                    {it.cheaper_elsewhere && cheap && (
                      <div className="text-xs text-[var(--danger)] flex items-start gap-1">
                        <Warning size={14} weight="fill" className="mt-0.5 shrink-0" />
                        <span>
                          Más barato en <strong>{cheap.supplier_name}</strong>: <span className="font-mono font-bold">{Number(cheap.price).toFixed(2)} €</span>
                          {cheap.invoice_date && <span className="text-[var(--text-secondary)]"> ({cheap.invoice_date})</span>}
                        </span>
                      </div>
                    )}
                  </td>
                  <td className="p-4 text-right no-print">
                    <button
                      onClick={() => hideItem(it)}
                      data-testid={`hide-item-${it.descripcion_norm}`}
                      className="inline-flex items-center gap-1 px-2 py-1 rounded-lg border border-[var(--border)] hover:bg-[var(--danger)] hover:text-white hover:border-[var(--danger)] text-xs font-display font-semibold"
                      title="Ocultar de este proveedor (reaparecerá si vuelve a salir en una factura)"
                    >
                      <Trash size={12} weight="duotone" />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
